// Caps and runtime settings: rate limits, spend caps and concurrency (design
// 9.4, 9.5), plus the few non-switch settings the admin edits. Stored in the
// D1 settings table like switches, not as config versions. The Sept 28 2026
// rule: the bot check, rate limits and spend caps exist BEFORE the public
// link points at v2.
//
// Every section has two schemas: the stored one (with defaults, so a missing
// row reads as the default) and a PATCH one (no defaults, so saving one
// section never rewrites another; the zod 4 partial-with-defaults trap).
import { z } from "zod";
import { behaviorText } from "./common";

const Usd = z.number().min(0);

/** Caps without defaults: the shape an admin sends when saving the caps section (all fields, no filling in). */
export const CapsValues = z.strictObject({
  // Check-in limits, claimed before the write (design 9.4).
  checkins_per_ip_hour: z.number().int().min(1),
  checkins_per_ip_day: z.number().int().min(1),
  checkins_per_email_day: z.number().int().min(1),
  daily_consult_ceiling: z.number().int().min(0),
  daily_spend_cap_usd: Usd,
  concurrency_cap: z.number().int().min(0),
  per_consult_notice_usd: Usd,
  per_consult_alert_usd: Usd,
  per_consult_close_usd: Usd,
  event_mode: z.strictObject({
    checkins_per_ip_hour: z.number().int().min(1),
    checkins_per_ip_day: z.number().int().min(1).optional(),
    daily_consult_ceiling: z.number().int().min(0).optional(),
    daily_spend_cap_usd: Usd.optional(),
  }),
});

export const Caps = z.object({
  // Check-in limits, claimed before the write (design 9.4).
  checkins_per_ip_hour: z.number().int().min(1).default(5),
  checkins_per_ip_day: z.number().int().min(1).default(15),
  checkins_per_email_day: z.number().int().min(1).default(3),
  /** Global daily ceiling on consults. 200 a month is about 7 a day (ESTIMATE). */
  daily_consult_ceiling: z.number().int().min(0).default(60),
  /** Daily spend cap; fails CLOSED: new check-ins stop, live calls finish (design 9.5). */
  daily_spend_cap_usd: Usd.default(40),
  /** Live consults at once. 8 to 12 on Gemini Tier 1, 25 on Tier 2 (design 9.5). */
  concurrency_cap: z.number().int().min(0).default(10),
  // Per consult in-call metered cost (design 9.5).
  per_consult_notice_usd: Usd.default(1.0),
  per_consult_alert_usd: Usd.default(1.8),
  per_consult_close_usd: Usd.default(2.25),
  /** Values used while the EVENT_MODE switch is on. Unset fields keep the normal value. */
  event_mode: z
    .object({
      checkins_per_ip_hour: z.number().int().min(1).default(40),
      checkins_per_ip_day: z.number().int().min(1).optional(),
      daily_consult_ceiling: z.number().int().min(0).optional(),
      daily_spend_cap_usd: Usd.optional(),
    })
    .prefault({}),
});
export type Caps = z.infer<typeof Caps>;

/** The caps in force, with EVENT_MODE applied. */
export function effectiveCaps(caps: Caps, eventMode: boolean): Omit<Caps, "event_mode"> {
  const { event_mode, ...base } = caps;
  if (!eventMode) return base;
  return {
    ...base,
    checkins_per_ip_hour: event_mode.checkins_per_ip_hour,
    checkins_per_ip_day: event_mode.checkins_per_ip_day ?? base.checkins_per_ip_day,
    daily_consult_ceiling: event_mode.daily_consult_ceiling ?? base.daily_consult_ceiling,
    daily_spend_cap_usd: event_mode.daily_spend_cap_usd ?? base.daily_spend_cap_usd,
  };
}

// ---------------------------------------------------------------------------
// The consent notice (design 9.2). Shown next to Start at check-in; its
// version is recorded with every consult. Counsel's wording is pending (D6),
// so the seeded text is a labeled v0 draft. Changing the text means a new
// version string, so what each consult agreed to stays traceable.
// ---------------------------------------------------------------------------

export const ConsentNotice = z.object({
  version: z.string().min(1).max(64),
  text: z.object({
    en: behaviorText(2000),
    /** Shown when the check-in is in Spanish; English is used when absent. */
    es: behaviorText(2000).optional(),
  }),
  draft_label: z.string().max(200).optional(),
});
export type ConsentNotice = z.infer<typeof ConsentNotice>;

// ---------------------------------------------------------------------------
// otto-app's model settings and the substance gate (moved here from otto-app
// settings rows at integration, Sept 28 2026, so the admin can edit them).
// ---------------------------------------------------------------------------

/**
 * Model ids for the calls otto-app makes outside the pinned config (design
 * D7: Claude Sonnet 5 writes the package, Gemini 3.8 Flash is the fallback).
 * The coach's model is in the pinned CoachConfig; the drafter's is the
 * DRAFTER_MODEL switch.
 */
export const ModelSettingsValues = z.strictObject({
  writer: z.string().min(1).max(100),
  writer_fallback: z.string().min(1).max(100),
  /** How long the writer may take (a queue job, nobody waiting). */
  writer_timeout_ms: z.number().int().min(5_000).max(300_000),
  drafter_timeout_ms: z.number().int().min(1_000).max(60_000),
});
export const ModelSettings = z.object({
  writer: ModelSettingsValues.shape.writer.default("claude-sonnet-5"),
  writer_fallback: ModelSettingsValues.shape.writer_fallback.default("gemini-3.8-flash"),
  // 240 s: on Wes's 12 minute test consult (Oct 20 2026) the writer timed out twice at 120 s.
  writer_timeout_ms: ModelSettingsValues.shape.writer_timeout_ms.default(240_000),
  drafter_timeout_ms: ModelSettingsValues.shape.drafter_timeout_ms.default(15_000),
});
export type ModelSettings = z.infer<typeof ModelSettings>;

/** The substance gate (design 8.2 step 4, decision D8): starting values, tuned after 50 real consults. */
export const SubstanceSettingsValues = z.strictObject({
  min_owner_talk_seconds: z.number().int().min(0).max(1800),
  min_must_share: z.number().min(0).max(1),
});
export const SubstanceSettings = z.object({
  min_owner_talk_seconds: SubstanceSettingsValues.shape.min_owner_talk_seconds.default(120),
  min_must_share: SubstanceSettingsValues.shape.min_must_share.default(0.2),
});
export type SubstanceSettings = z.infer<typeof SubstanceSettings>;

/**
 * Which held consults are real leads (Wes, Oct 18 2026): a dropped or short
 * call gets a "finish your consult" link only when the owner meant it. Not a
 * lead: a call the owner ended themselves (only the end reasons listed here
 * count; the default is a dropped connection or silence), or one with almost
 * no answers.
 */
export const UNFINISHED_END_REASONS = ["connection_lost", "silence_timeout", "time_limit", "error", "spend_cap"] as const;
export const UnfinishedSettingsValues = z.strictObject({
  min_owner_talk_seconds: z.number().int().min(0).max(1800),
  min_owner_answers: z.number().int().min(0).max(50),
  end_reasons: z.array(z.enum(UNFINISHED_END_REASONS)).max(UNFINISHED_END_REASONS.length),
  /** How long the emailed link to finish works. */
  resume_days: z.number().int().min(1).max(30),
});
export const UnfinishedSettings = z.object({
  min_owner_talk_seconds: UnfinishedSettingsValues.shape.min_owner_talk_seconds.default(30),
  min_owner_answers: UnfinishedSettingsValues.shape.min_owner_answers.default(2),
  end_reasons: UnfinishedSettingsValues.shape.end_reasons.default(["connection_lost", "silence_timeout"]),
  resume_days: UnfinishedSettingsValues.shape.resume_days.default(14),
});
export type UnfinishedSettings = z.infer<typeof UnfinishedSettings>;

const HttpsUrl = z.url({ protocol: /^https$/ });
/**
 * The handoff target: https, or plain http on this machine only (localhost or
 * 127.0.0.1), so the consult can hand a package to a hub running locally
 * (PLAN P2.12, W3 CR-006, Oct 12 2026). A Worker in production cannot reach
 * localhost, so the exception never carries a package off the machine.
 */
const HandoffUrl = z
  .url({ protocol: /^https?$/ })
  .refine((u) => u.startsWith("https://") || /^http:\/\/(localhost|127\.0\.0\.1)(:\d+)?(\/|$)/.test(u), {
    message: "Use https, or http on localhost for a local test",
  });

export const RuntimeSettings = z.object({
  caps: Caps.prefault({}),
  /** Where HANDOFF_ENABLED posts the package. null until Wes sets it. */
  handoff_webhook_url: HandoffUrl.nullable().default(null),
  /** Where ROUTE_TO_LEGACY sends the check-in. */
  legacy_consult_url: HttpsUrl.nullable().default(null),
  /** The check-in's consent notice. null: the check-in stays CLOSED (it must never run without one). */
  consent_notice: ConsentNotice.nullable().default(null),
  /** The privacy page linked beside the consent box. null: the web app's own default link. */
  privacy_url: HttpsUrl.nullable().default(null),
  /** "Prefer not to talk?" and the thank-you page's callback link. null: the web app's own default link. */
  callback_url: HttpsUrl.nullable().default(null),
  models: ModelSettings.prefault({}),
  substance: SubstanceSettings.prefault({}),
  unfinished: UnfinishedSettings.prefault({}),
  /** Where an unfinished consult (a real lead, CR-015) is posted. null: held, nothing sent. */
  unfinished_webhook_url: HandoffUrl.nullable().default(null),
});
export type RuntimeSettings = z.infer<typeof RuntimeSettings>;
export type RuntimeSettingsKey = keyof RuntimeSettings;
export const RUNTIME_SETTING_KEYS = Object.keys(RuntimeSettings.shape) as RuntimeSettingsKey[];

/**
 * A settings patch: each section present is replaced WHOLE, and nothing
 * absent is touched or defaulted. Unknown sections are refused.
 */
export const RuntimeSettingsPatch = z.strictObject({
  caps: CapsValues.optional(),
  handoff_webhook_url: HandoffUrl.nullable().optional(),
  legacy_consult_url: HttpsUrl.nullable().optional(),
  consent_notice: ConsentNotice.nullable().optional(),
  privacy_url: HttpsUrl.nullable().optional(),
  callback_url: HttpsUrl.nullable().optional(),
  models: ModelSettingsValues.optional(),
  substance: SubstanceSettingsValues.optional(),
  unfinished: UnfinishedSettingsValues.optional(),
  unfinished_webhook_url: HandoffUrl.nullable().optional(),
});
export type RuntimeSettingsPatch = z.infer<typeof RuntimeSettingsPatch>;
